<template>
    <div class="display-flex flex-column align-items-end select-tech-areas-container">
        <span class="select-tech-areas-link link small" @click="openTechAreaSelector()">
            <font-awesome-icon :icon="['fas', 'pencil']" class="mr-sm" />Manage technology space
        </span>
        <div
            class="position-relative tooltip-container"
            @mouseenter="showTooltip = true"
            @mouseleave="showTooltip = false"
        >
            <p class="mt-sm small tech-area-count dotted-underline">
                {{ moat2ProductStore.productAlignmentTechAreaSelections.size }} selected of
                {{ moat2ProductStore.allTechAreaIds.length }}
            </p>
            <div
                v-if="showTooltip && moat2ProductStore.productAlignmentTechAreaSelections.size > 0"
                class="tech-areas-tooltip"
            >
                <h6 class="tooltip-header">Selected Technology Areas</h6>
                <div class="tooltip-content">
                    <!-- Display tech areas directly if grouping doesn't work -->
                    <template v-if="selectedTechAreasGrouped.length === 0">
                        <ul class="tech-area-list-flat">
                            <li
                                v-for="(techArea, index) in [
                                    ...moat2ProductStore.productAlignmentTechAreaSelections,
                                ].slice(0, 10)"
                                :key="index"
                            >
                                <p>{{ techArea.displayName }}</p>
                            </li>
                        </ul>
                    </template>

                    <!-- Display grouped by product if data is available -->
                    <template v-else>
                        <div
                            v-for="(productGroup, groupIndex) in selectedTechAreasGrouped"
                            :key="`product-${groupIndex}`"
                            class="product-group"
                        >
                            <p class="product-name bold">{{ productGroup.productName }}</p>
                            <ul class="tech-area-list">
                                <li
                                    v-for="(techArea, techIndex) in productGroup.techAreas.slice(
                                        0,
                                        5
                                    )"
                                    :key="`tech-${techIndex}`"
                                >
                                    <p>{{ techArea.displayName }}</p>
                                </li>
                                <li
                                    v-if="productGroup.techAreas.length > 5"
                                    class="more-tech-areas"
                                >
                                    + {{ productGroup.techAreas.length - 5 }} more
                                </li>
                            </ul>
                        </div>
                    </template>

                    <div
                        v-if="moat2ProductStore.productAlignmentTechAreaSelections.size > 0"
                        class="more-items"
                    >
                        <span class="more-items-link link" @click="openTechAreaSelector()">
                            View all technology areas
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <AonModal
            v-if="showTechAreaSelector"
            col-width="10"
            :title="`Manage Technology Space - ${entityStore.entity?.name}`"
            @close="applyTechAreaChanges"
            @click:outside="applyTechAreaChanges"
        >
            <div
                class="tech-area-modal-content display-flex flex-column flex-grow-1"
                style="height: inherit"
            >
                <OutputTechAreaTable
                    :table-data="productAlignmentSummaryData"
                    :loading="techAreaTableLoading"
                    :entity-name="entityStore.entity?.name"
                    :done-loading-all="techAreaTableLoading"
                    type="pa"
                    :use-store-selections="true"
                    height="600px"
                >
                    <template #above-table>
                        <div
                            class="above-table-content ml-xl display-flex flex-wrap justify-end mt-n1"
                        >
                            <p class="mt-sm mr-xl small">
                                {{ moat2ProductStore.productAlignmentTechAreaSelections.size }}
                                selected of
                                {{ moat2ProductStore.allTechAreaIds.length }}
                            </p>
                            <AonButton
                                style="min-width: 250px"
                                label="Customize in Arenas"
                                text-with-icon
                                :icon-options="{
                                    iconStyle: 'fas',
                                    iconName: 'fa-swords',
                                }"
                                :class="{ disabled: !productAlignmentSummaryData.length }"
                                :loading="techAreaTableLoading || arenaSetupLoading"
                                load-color="white"
                                @clicked="setupArena()"
                            />
                        </div>
                    </template>
                </OutputTechAreaTable>
                <div class="d-flex justify-content-end mt-3 pr-3">
                    <AonButton label="Ok" @clicked="applyTechAreaChanges" />
                </div>
            </div>
        </AonModal>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useEntityStore, useMoat2ProductStore, useArenaStore } from '@/stores'
import { getProductAlignmentSummary } from '@/api/productAlignment.js'
import OutputTechAreaTable from '@/components/alignmentOutput/techAreas/OutputTechAreaTable.vue'
import { inject } from 'vue'
import { requestArena } from '@/api/arena.js'

const props = defineProps({
    techAreaTableData: {
        type: Array,
        default: () => [],
    },
})

const emit = defineEmits(['tech-areas-changed'])

const entityStore = useEntityStore()
const moat2ProductStore = useMoat2ProductStore()
const logger = inject('logger')

const router = useRouter()
const arenaStore = useArenaStore()
const arenaSetupLoading = ref(false)
const showTechAreaSelector = ref(false)
const showTooltip = ref(false)
const techAreaTableLoading = ref(false)
const productAlignmentSummaryData = ref([])
const lastSelectedTechAreas = ref(new Set())

// Group tech areas by product
const selectedTechAreasGrouped = computed(() => {
    const selections = [...moat2ProductStore.productAlignmentTechAreaSelections]

    const productMap = new Map()

    selections.forEach((techArea) => {
        let productName = 'Uncategorized'

        const parentNodeId = techArea.parentNodeId || techArea.parent

        if (parentNodeId) {
            const parentNode = moat2ProductStore.productAlignmentProducts.find(
                (p) => p.nodeId === parentNodeId
            )
            if (parentNode) {
                productName = parentNode.to
            } else {
            }
        } else {
        }

        if (!productMap.has(productName)) {
            productMap.set(productName, {
                productName,
                techAreas: [],
            })
        }

        productMap.get(productName).techAreas.push(techArea)
    })

    const result = [...productMap.values()].sort((a, b) =>
        a.productName.localeCompare(b.productName)
    )

    return result
})

const openTechAreaSelector = () => {
    showTechAreaSelector.value = true
    lastSelectedTechAreas.value = new Set([...moat2ProductStore.productAlignmentTechAreaSelections])
    loadTechAreaData()
}

const loadTechAreaData = async () => {
    if (productAlignmentSummaryData.value.length > 0) {
        return
    }
    techAreaTableLoading.value = true

    try {
        const { data } = await getProductAlignmentSummary(
            moat2ProductStore.alignmentPk,
            [...moat2ProductStore.productAlignmentTechAreaNodes].map((item) => item.nodeId)
        )
        productAlignmentSummaryData.value = data.nodes
    } catch (err) {
        logger.error(err)
    } finally {
        techAreaTableLoading.value = false
    }
}

const applyTechAreaChanges = async () => {
    showTechAreaSelector.value = false

    if (
        !areSetsEqual(
            lastSelectedTechAreas.value,
            moat2ProductStore.productAlignmentTechAreaSelections
        )
    ) {
        lastSelectedTechAreas.value = new Set([
            ...moat2ProductStore.productAlignmentTechAreaSelections,
        ])
        emit('tech-areas-changed')
    }
}

const areSetsEqual = (setA, setB) => {
    if (setA?.size !== setB?.size) return false
    return [...setA].every((item) => setB.has(item))
}

const setupArena = async () => {
    arenaSetupLoading.value = true

    let selectedProducts = moat2ProductStore.productAlignmentProducts.filter((prods) => {
        return [...moat2ProductStore.productAlignmentTechAreaSelections].find(
            (tech) => tech.parentNodeId === prods.nodeId
        )
    })

    try {
        const { data } = await requestArena(arenaStore.rootPAId, [
            ...moat2ProductStore.techAreaIdCollection,
            ...selectedProducts.map((item) => item.nodeId),
        ])

        router.push({ name: 'Arena', params: { arenaSourceId: data.productAlignmentId } })
    } catch (err) {
        logger.error(err)
    } finally {
        arenaSetupLoading.value = false
    }
}
</script>

<style lang="scss" scoped>
.select-tech-areas-container {
    position: absolute;
    top: 30px;
    right: 30px;
}

.tooltip-container {
    display: inline-block;
}

.tech-area-count {
    cursor: pointer;
    &:hover {
        text-decoration: underline;
    }
}

:deep(.a-modal .container) {
    max-height: 850px !important;

    overflow: hidden !important;
}

.tech-areas-tooltip {
    position: absolute;
    z-index: 1000;
    top: 25px;
    right: 0;
    width: 350px;
    max-height: 400px;
    background-color: white;
    border-radius: 4px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
    overflow: hidden;
    pointer-events: auto; /* Ensures the tooltip receives mouse events */

    .tooltip-header {
        padding: 10px;
        font-weight: bold;
        background-color: #f5f5f5;
        border-bottom: 1px solid #e0e0e0;
    }

    .tooltip-content {
        max-height: 350px;
        overflow-y: auto;
        padding: 10px;

        .tech-area-list-flat {
            list-style-type: none;
            padding: 0;
            margin: 0;

            li {
                padding: 6px 0;
                border-bottom: 1px solid #f0f0f0;
                font-size: 0.9em;
                color: #444;

                &:last-child {
                    border-bottom: none;
                }
            }
        }

        .product-group {
            margin-bottom: 15px;
            padding-bottom: 5px;
            border-bottom: 1px solid #e0e0e0;

            &:last-child {
                border-bottom: none;
            }

            .product-name {
                font-weight: bold;
                margin-bottom: 5px;
                color: #333;
            }

            .tech-area-list {
                list-style-type: none;
                padding: 0 0 0 10px;
                margin: 0;

                li {
                    padding: 4px 0;
                    font-size: 0.9em;
                    color: #444;

                    &.more-tech-areas {
                        font-style: italic;
                        color: #666;
                        text-align: right;
                        padding-top: 5px;
                        font-size: 0.85em;
                    }
                }
            }
        }

        .more-items {
            text-align: center;
            padding: 8px;
            margin-top: 5px;
            border-top: 1px solid #e0e0e0;

            .more-items-link {
                cursor: pointer;
                color: #0066cc;

                &:hover {
                    text-decoration: underline;
                }
            }
        }
    }
}

.tech-area-modal-content {
    width: 100%;
    height: 850px;
    max-height: 850px;
    overflow: hidden;
}

.above-table-content {
    align-items: end;
}
</style>
